<script>
export default {
  name: "HomeGoalProgress",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    status: {
      type: String,
      default: "inactive" // 'active' | 'inactive'
    },
    current: {
      type: Number,
      default: 0
    },
    target: {
      type: Number,
      default: 0
    },
    currentRate: {
      type: Number,
      default: 0
    },
    targetRate: {
      type: Number,
      default: 0
    },
    remaining: {
      type: String,
      default: ""
    },
    potentialTotal: {
      type: Number,
      default: 0
    },
    totalReceived: {
      type: Number,
      default: 0
    }
  },
  computed: {
    progressPercent() {
      if (!this.target) return 0;
      return Math.min(100, Math.round((this.current / this.target) * 100));
    },
    hasReceived() {
      return this.totalReceived > 0;
    }
  }
};
</script>

<template>
  <div class="card">
    <div class="card-body">
      <template v-if="loading">
        <div class="d-flex mb-3">
          <div class="skeleton-block skeleton-pill" style="width: 70px; height: 22px;"></div>
        </div>

        <div class="d-flex align-items-baseline justify-content-between mb-2">
          <span class="font-size-14"><span class="skeleton-block" style="width: 220px; height: 14px;"></span></span>
          <span class="progress-percent"><span class="skeleton-block" style="width: 36px; height: 13px;"></span></span>
        </div>

        <div class="goal-progress-track mb-1">
          <div class="skeleton-block" style="width: 100%; height: 100%; border-radius: 999px;"></div>
        </div>
        <div class="d-flex justify-content-between font-size-11 mb-4">
          <span v-for="n in 5" :key="n" class="skeleton-block" style="width: 14px; height: 10px;"></span>
        </div>

        <div class="d-flex align-items-center flex-wrap goal-compare">
          <div class="goal-compare-box flex-fill">
            <div class="font-size-12 text-uppercase mb-1"><span class="skeleton-block" style="width: 110px; height: 10px;"></span></div>
            <div class="goal-compare-value"><span class="skeleton-block" style="width: 80px; height: 22px;"></span></div>
          </div>
          <i class="bx bx-right-arrow-alt goal-arrow"></i>
          <div class="goal-compare-box goal-compare-box-highlight flex-fill">
            <div class="font-size-12 text-uppercase mb-1"><span class="skeleton-block" style="width: 110px; height: 10px;"></span></div>
            <div class="goal-compare-value"><span class="skeleton-block" style="width: 80px; height: 22px;"></span></div>
          </div>
        </div>

        <div class="goal-alert mt-3">
          <div class="font-size-12 text-uppercase mb-1"><span class="skeleton-block" style="width: 180px; height: 10px;"></span></div>
          <div class="goal-alert-value"><span class="skeleton-block" style="width: 110px; height: 20px;"></span></div>
        </div>

        <div class="goal-total mt-3">
          <div class="font-size-12 text-uppercase mb-1"><span class="skeleton-block" style="width: 130px; height: 10px;"></span></div>
          <div class="goal-total-value"><span class="skeleton-block" style="width: 100px; height: 20px;"></span></div>
        </div>
      </template>

      <template v-else>
        <div class="d-flex mb-3">
          <div class="badge-pill" :class="status == 'active' ? 'badge-pill-success' : 'badge-pill-danger'">
            {{ status == 'active' ? 'Ativo' : 'Inativo' }}
          </div>
        </div>

        <div class="d-flex align-items-baseline justify-content-between mb-2">
          <span class="font-size-14 font-weight-semibold">
            {{ current | points }}/{{ target | points }} <span class="text-muted font-weight-normal">de assinaturas no mês</span>
          </span>
          <span class="progress-percent notranslate">{{ progressPercent }}%</span>
        </div>

        <div class="goal-progress-track mb-1">
          <div class="goal-progress-bar" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="d-flex justify-content-between font-size-11 text-muted mb-4">
          <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
        </div>

        <div class="d-flex align-items-center flex-wrap goal-compare">
          <div class="goal-compare-box flex-fill">
            <div class="font-size-12 font-weight-semibold text-muted text-uppercase">Valor atual por venda</div>
            <div class="goal-compare-value notranslate">{{ currentRate | currency }}</div>
          </div>
          <i class="bx bx-right-arrow-alt goal-arrow"></i>
          <div class="goal-compare-box goal-compare-box-highlight flex-fill">
            <div class="font-size-12 font-weight-semibold text-uppercase">Valor ao atingir meta</div>
            <div class="goal-compare-value goal-compare-value-highlight notranslate">{{ targetRate | currency }}</div>
          </div>
        </div>

        <div v-if="remaining" class="goal-alert mt-3" :class="potentialTotal > 0 ? 'goal-alert-warning' : ''">
          <div class="font-size-12 font-weight-semibold text-uppercase">Se atingir a meta, você recebe</div>
          <div class="goal-alert-value notranslate">{{ potentialTotal | currency }}</div>
        </div>

        <div class="goal-total mt-3" :class="{ 'goal-total-empty': !hasReceived }">
          <div class="font-size-12 font-weight-semibold text-uppercase">Total já recebido</div>
          <div class="goal-total-value notranslate">{{ totalReceived | currency }}</div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.progress-percent {
  font-size: 13px;
  font-weight: 600;
  color: #065f46;
}

.goal-progress-track {
  width: 100%;
  height: 12px;
  border-radius: 999px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.goal-progress-bar {
  height: 100%;
  border-radius: 999px;
  background: #065f46;
}

.goal-compare-box {
  min-width: 180px;
  padding: 16px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  margin: 4px;
}

.goal-compare-box-highlight {
  background: #ecfdf5;
  border: 1.5px solid #065f46;
}

.goal-compare-value {
  font-size: 22px;
  font-weight: 600;
}

.goal-compare-value-highlight {
  color: #065f46;
}

.goal-arrow {
  font-size: 20px;
  color: #9ca3af;
  margin: 4px;
}

.goal-alert {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}

.goal-alert-warning {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.goal-alert-value {
  font-size: 20px;
  font-weight: 600;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.badge-pill::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.badge-pill-success {
  background-color: #ecfdf5;
  color: #065f46;
}

.badge-pill-warning {
  background-color: #fffbeb;
  color: #92400e;
}

.badge-pill-danger {
  background-color: #fef2f2;
  color: #991b1b;
}

.goal-total {
  padding: 14px 16px;
  border-radius: 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
}

.goal-total-value {
  font-size: 20px;
  font-weight: 600;
}

.goal-total-empty {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #6b7280;
}

.skeleton-block {
  display: inline-block;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f2 25%, #e2e4e7 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.skeleton-pill {
  border-radius: 999px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
</style>
